import { formatPercent, NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AccountMarketplace, AccountSubType } from "@front/m19-api-client";
import { Metric, MetricFormatPipe, MetricType, SPLITABLE_METRICS } from "@front/m19-metrics";
import { CurrencyStat } from "@front/m19-models";
import { AccountSelectionService, AdStatsType, AuthService, UserSelectionService } from "@front/m19-services";
import {
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  IMetricEvoComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { MetricsMenuComponent } from "@m19-board/insights/metric-selector/metrics-menu/metrics-menu.component";
import { AccountSettingsV2Service } from "@m19-board/services/account-settings.service";
import { map, take } from "rxjs";
import { MetricDescriptionPipe } from "@front/m19-utils";
import { CdkMenuTrigger } from "@angular/cdk/menu";

@Component({
  selector: "app-metric-card",
  imports: [
    IButtonDirective,
    MetricFormatPipe,
    TranslocoDirective,
    NgClass,
    MetricsMenuComponent,
    IMetricEvoComponent,
    ITooltipDirective,
    IIconComponent,
    MetricDescriptionPipe,
    IDropdownMenuTriggerDirective,
  ],
  template: `
    <div
      class="group relative flex h-full overflow-hidden border-r-1 border-gray-100"
      (click)="selectMetric()"
      [class.selected]="isSelected()"
      [class.rounded-l-2xl]="isFirst()"
      *transloco="let t"
    >
      @if (!isSelected() && removable()) {
        <span
          class="absolute right-0 m-1 hidden text-lg text-gray-600 group-hover:flex hover:text-gray-800"
          (click)="removeMetric()"
          [iTooltip]="'Remove metric'"
          ><IIcon name="close"
        /></span>
      }
      <div
        class="card-body p-1 text-center"
        [ngClass]="{
          'border-b-[3px]': isSelected(),
        }"
        [style.border-bottom-color]="metric().color"
      >
        @if (metric()) {
          <button
            [iDropdownMenuTrigger]="metricsMenu"
            placement="bottom-center"
            (click)="$event.stopPropagation()"
            class="relative"
          >
            <div
              class="pointer type-body-medium flex items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap text-gray-500 hover:text-gray-700"
              [iTooltip]="metric() | metricDescription"
              iTooltipPlacement="top-center"
            >
              <div>
                <div class="text-base">{{ t("metrics." + metric().id + "_title") }}</div>
              </div>
              <IIcon name="chevronDown" class="absolute top-0.5 -right-6 z-10 text-lg text-gray-700" />
            </div>
          </button>
        }
        <div class="relative">
          <div
            class="flex w-full flex-col items-center justify-center min-[2000px]:flex-row"
            [ngClass]="
              shouldRequestSellingPartnerGrant() && metric().requireSellingPartnerAccess
                ? 'pointer-events-none opacity-50 blur-sm'
                : ''
            "
          >
            <div class="mb-0 overflow-hidden py-1 text-xl text-ellipsis whitespace-nowrap" iTooltipPlacement="bottom">
              @if (dataLoading()) {
                <div class="mx-auto my-1 h-6 w-16 animate-pulse rounded-md bg-gray-200"></div>
              } @else {
                <div [iTooltip]="tooltip()" class="mx-1 flex-1 items-center">
                  {{ metric() | metricFormatPipe: data() : locale() : currency() : undefined : undefined }}
                  @if (this.splitData() !== undefined && isSplitable()) {
                    <span class="-ml-1 items-center justify-center align-middle">
                      <IIcon name="infoCircle" class="scale-75" />
                    </span>
                  }
                </div>
              }
            </div>
            @if (withPeriodComparison() && !dataLoading()) {
              @if (!loadingPreviousPeriodMetrics()) {
                <IMetricEvo
                  class="ml-2"
                  [metric]="metric()"
                  [currency]="currency()"
                  [locale]="locale()"
                  [currentData]="data()"
                  [previousData]="previousPeriodData()!"
                  [rawTooltip]="previousPeriodTooltip()"
                  displayMode="badge"
                />
              } @else {
                <div class="ml-2 text-center">(-)</div>
              }
            }
          </div>
          @if (shouldRequestSellingPartnerGrant() && metric().requireSellingPartnerAccess) {
            <div class="absolute top-1 w-full">
              <button
                iButton
                color="orange"
                (click)="requestGrant()"
                [iTooltip]="'Grant access to organic data to view ' + metric().title + ' trend'"
              >
                {{ t("common.grant_access") }}
              </button>
            </div>
          }
        </div>
      </div>
    </div>
    <ng-template #metricsMenu>
      <app-metrics-menu
        [availableMetrics]="availableMetrics()"
        [isNewMetricSelector]="false"
        [metricsInSelector]="metricsInSelector()"
        (toggleMetric)="toggleOtherMetricFromMenu($event)"
      />
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricCardComponent<T extends CurrencyStat> {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly accountSettingsService = inject(AccountSettingsV2Service);
  private readonly router = inject(Router);
  private userSelectionService = inject(UserSelectionService);
  private authService = inject(AuthService);

  metricsMenuTrigger = viewChild(CdkMenuTrigger);

  metric = input.required<Metric<T>>();
  selectedMetrics = input.required<Metric<T>[]>();
  availableMetrics = input.required<Metric<T>[]>();
  metricsInSelector = input.required<Metric<T>[]>();
  data = input.required<T>();
  splitData = input<{ splitData1: T; splitData2: T; type: AdStatsType } | undefined>(undefined);
  previousSplitData = input<{ splitData1: T; splitData2: T; type: AdStatsType } | undefined>(undefined);
  previousPeriodData = input<T | undefined>(undefined);
  withPeriodComparison = input<boolean>(false);
  loadingPreviousPeriodMetrics = input<boolean>(false);
  shouldRequestSellingPartnerGrant = input.required<boolean>();
  isFirst = input(false);
  dataLoading = input<boolean>(false);

  clickedMetric = output();
  removedMetric = output();
  toggledOtherMetricFromMenu = output<Metric<T>>();

  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });

  isSelected = computed(() => this.selectedMetrics().includes(this.metric()));
  removable = computed(() => !this.selectedMetrics().includes(this.metric()) && this.metricsInSelector().length > 1);
  tooltip = computed(() => this.getTooltipForMetric(this.metric()));
  isSplitable = computed(() => this.isSplitableMetric(this.metric()));
  previousPeriodTooltip = computed(() =>
    this.getTooltipForPreviousPeriodMetric(
      this.metric(),
      this.previousPeriodData(),
      this.previousSplitData(),
      this.isSplitable(),
    ),
  );

  selectMetric() {
    this.clickedMetric.emit();
  }

  removeMetric() {
    this.removedMetric.emit();
  }

  toggleOtherMetricFromMenu(m: Metric<T>) {
    this.toggledOtherMetricFromMenu.emit(m);
    this.metricsMenuTrigger()?.close();
  }

  requestGrant() {
    this.accountSelectionService.accountMarketplacesSelection$
      .pipe(
        map((accountMarketplaces: AccountMarketplace[]) => accountMarketplaces.filter((am) => !am.isValidToken)),
        take(1),
      )
      .subscribe((invalidAccountMarketplaces) => {
        if (invalidAccountMarketplaces.length == 1) {
          const am = invalidAccountMarketplaces[0];
          this.accountSettingsService.grantSellingPartnerAccess(
            am.accountId,
            am.marketplace,
            am.accountType!,
            am.accountSubType === AccountSubType.KDP_AUTHOR,
          );
        } else {
          this.router.navigate(["accounts"]);
        }
      });
  }

  private isSplitableMetric(metric: Metric<T>): boolean {
    return SPLITABLE_METRICS.has(metric as Metric<CurrencyStat>);
  }

  private getTooltipForMetric(metric: Metric<T>) {
    if (!this.splitData() || !this.isSplitableMetric(metric)) {
      return metric.format(this.data(), this.locale(), this.currency());
    }
    return this.getSplitTooltip(metric, this.splitData());
  }

  private getTooltipForPreviousPeriodMetric(
    metric: Metric<T>,
    previousPeriodData: T | undefined,
    previousSplitData: { splitData1: T; splitData2: T; type: AdStatsType } | undefined,
    isSplitable: boolean,
  ) {
    if (!previousSplitData || !isSplitable) {
      return metric.format(previousPeriodData!, this.locale(), this.currency());
    }
    return this.getSplitTooltip(metric, previousSplitData);
  }

  private getSplitTooltip(
    metric: Metric<T>,
    splitData: { splitData1: T; splitData2: T; type: AdStatsType } | undefined,
  ) {
    const split1Wording = AdStatTypeChartConfig[splitData!.type].suffix;
    const split2Wording = AdStatTypeChartConfig[getOppositeAdStatsType(splitData!.type)].suffix;

    if (metric.type == MetricType.SUMMABLE) {
      const split1Percent =
        (metric.value(splitData!.splitData1) ?? 0) /
        ((metric.value(splitData!.splitData2) ?? 0) + (metric.value(splitData!.splitData1) ?? 0));

      const split2Percent = 1 - split1Percent;
      const split1PercentFormatted = isNaN(split1Percent) ? "-" : formatPercent(split1Percent, this.locale(), "1.0");
      const split2PercentFormatted = isNaN(split2Percent) ? "-" : formatPercent(split2Percent, this.locale(), "1.0");

      return `${split1Wording}: ${metric.format(splitData!.splitData1, this.locale(), this.currency())} (${split1PercentFormatted})\n${split2Wording}: ${metric.format(splitData!.splitData2, this.locale(), this.currency())} (${split2PercentFormatted})`;
    } else {
      return `${split1Wording}: ${metric.format(splitData!.splitData1, this.locale(), this.currency())}\n${split2Wording}: ${metric.format(splitData!.splitData2, this.locale(), this.currency())}`;
    }
  }
}

function getOppositeAdStatsType(type: AdStatsType): AdStatsType {
  if (type === AdStatsType.MANAGED) return AdStatsType.UNMANAGED;
  if (type === AdStatsType.AUDIENCE) return AdStatsType.NOT_IN_AUDIENCE;
  return AdStatsType.BASE;
}

const AdStatTypeChartConfig: Record<AdStatsType, { suffix: string; dashed?: boolean }> = {
  [AdStatsType.BASE]: {
    suffix: "",
    dashed: false,
  },
  [AdStatsType.MANAGED]: {
    suffix: "Managed",
    dashed: false,
  },
  [AdStatsType.UNMANAGED]: {
    suffix: "Unmanaged",
    dashed: true,
  },
  [AdStatsType.AUDIENCE]: {
    suffix: "Audience",
    dashed: false,
  },
  [AdStatsType.NOT_IN_AUDIENCE]: {
    suffix: "Not in Audience",
    dashed: true,
  },
  [AdStatsType.PREVIOUS_PERIOD]: {
    suffix: "Previous Period",
    dashed: true,
  },
};
